import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, message, Tag, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import PageSkeleton, { DetailSkeleton } from '../components/Skeleton';

const GOLD = '#B8892A';
const TEAL = '#1A6B54';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';
const RED = '#8C1F1F';

const GRADE_INFO = {
  S: { label: 'S级 · 全新/未使用', short: '未使用', color: '#8C1F1F', desc: '全新品相，无任何使用痕迹' },
  A: { label: 'A级 · 近新', short: '近新品', color: '#B8892A', desc: '轻微使用痕迹，整体品相优秀' },
  B: { label: 'B级 · 良好', short: '美品', color: '#5C7A3A', desc: '有一定使用痕迹，功能完好' },
  C: { label: 'C级 · 有瑕疵', short: '可用', color: '#666', desc: '明显使用痕迹或瑕疵，已如实标注' },
};

const CONDITION_AREAS = [
  { area: '外观整体', key: 'overall' },
  { area: '正面/表盘', key: 'front' },
  { area: '背面/底盖', key: 'back' },
  { area: '内里/机芯', key: 'interior' },
  { area: '五金/配件', key: 'hardware' },
  { area: '附件', key: 'accessories' },
];

function conditionForGrade(grade) {
  const map = {
    S: { overall: '全新', front: '无痕迹', back: '无痕迹', interior: '全新', hardware: '全新', accessories: '齐全' },
    A: { overall: '极美品', front: '极轻微', back: '极轻微', interior: '良好', hardware: '微氧化', accessories: '部分齐全' },
    B: { overall: '美品', front: '轻微擦痕', back: '有使用痕', interior: '正常', hardware: '轻微磨损', accessories: '部分缺失' },
    C: { overall: '可用', front: '明显痕迹', back: '明显痕迹', interior: '有瑕疵', hardware: '有磨损', accessories: '缺失' },
  };
  return map[grade] || map['A'];
}

// Estimate market price (roughly 1.5-2x of our price for effect)
function estimateMarketPrice(price) {
  return Math.round(price * 1.7 / 100) * 100;
}

function discountPercent(price, market) {
  if (!market) return '';
  return (price / market * 10).toFixed(1) + '折';
}

export default function MarketDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [activeTab, setActiveTab] = useState('detail');
  const [offerPrice, setOfferPrice] = useState('');
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/market/${id}`);
        setItem(res.data);
        if (res.data.category) {
          api.get('/market', { params: { category: res.data.category, limit: 4 } })
            .then(r => setSimilar((r.data.data || []).filter(x => x.id !== id).slice(0, 4)))
            .catch(() => {});
        }
      } catch {
        message.error(t('marketDetail.itemNotFound'));
        navigate('/market');
      } finally { setLoading(false); }
    })();
  }, [id, navigate]);

  if (loading) return <DetailSkeleton />;
  if (!item) return null;

  const photos = item.photos?.length > 0
    ? item.photos
    : [`https://placehold.co/600x600/F5EDD8/B8892A?text=${encodeURIComponent(item.brand || '?')}`];
  const grade = GRADE_INFO[item.item_grade] || null;
  const isOffer = item.pricing_mode === 'offer';
  const condition = conditionForGrade(item.item_grade);
  const hasExhPrice = item.exhibition_price && Number(item.exhibition_price) > 0;
  const effectivePrice = hasExhPrice ? Number(item.exhibition_price) : Number(item.price_cny);
  const priceCny = effectivePrice;
  const originalPrice = Number(item.price_cny);
  const marketPrice = estimateMarketPrice(originalPrice);
  const discount = discountPercent(priceCny, marketPrice);

  const handleOffer = async () => {
    if (!isLoggedIn) { navigate('/login'); return; }
    if (!offerPrice || Number(offerPrice) <= 0) { message.warning(t('marketDetail.invalidOffer')); return; }
    setSubmitting(true);
    try {
      await api.post(`/market/${id}/offer`, { offer_price_cny: Number(offerPrice) });
      message.success(t('marketDetail.offerSubmitted'));
      setOfferModalOpen(false);
    } catch (e) {
      message.error(e.response?.data?.error || t('marketDetail.offerFailed'));
    } finally { setSubmitting(false); }
  };

  const handleBuy = () => {
    if (!isLoggedIn) { navigate('/login'); return; }
    navigate(`/market/${id}/checkout`);
  };

  const px = isMobile ? 0 : 48;

  const TABS = [
    { key: 'detail', label: t('marketDetail.tabDetail') },
    { key: 'appraisal', label: t('marketDetail.tabAppraisal') },
    { key: 'specs', label: t('marketDetail.tabSpecs') },
    { key: 'similar', label: t('marketDetail.tabSimilar') },
  ];

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: isMobile ? 130 : 40 }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${px}px` }}>
        {/* Back */}
        <div style={{ padding: '12px 16px' }}>
          <button onClick={() => navigate('/market')} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: MUTED, fontSize: 13,
          }}>← {t('marketDetail.backToMarket')}</button>
        </div>

        <div style={{ display: isMobile ? 'block' : 'flex', gap: 32 }}>
          {/* LEFT: Image Gallery */}
          <div style={{ flex: '0 0 480px', maxWidth: isMobile ? '100%' : 480 }}>
            <div style={{
              width: '100%', paddingTop: '100%', position: 'relative',
              background: '#F5EDD8', borderRadius: isMobile ? 0 : 8, overflow: 'hidden',
              border: isMobile ? 'none' : `1px solid ${BD}`,
            }}>
              <img src={photos[activeImg]} alt="" style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover',
              }} />
              {/* Brand overlay */}
              {item.brand && (
                <div style={{
                  position: 'absolute', top: 12, left: 12, padding: '4px 10px', borderRadius: 4,
                  background: 'rgba(255,255,255,.85)', color: INK, fontSize: 12, fontWeight: 600,
                  fontFamily: "'Georgia', serif", letterSpacing: 0.5,
                }}>{item.brand}</div>
              )}
              {/* Image counter */}
              <div style={{
                position: 'absolute', bottom: 12, right: 12, padding: '3px 10px', borderRadius: 12,
                background: 'rgba(0,0,0,.5)', color: '#fff', fontSize: 11,
              }}>{t('marketDetail.actualPhoto')} {activeImg + 1}/{photos.length}</div>
            </div>
            {photos.length > 1 && (
              <div style={{ display: 'flex', gap: 6, padding: '10px 0', overflowX: 'auto' }}>
                {photos.map((p, i) => (
                  <div key={i} onClick={() => setActiveImg(i)} style={{
                    width: 56, height: 56, borderRadius: 4, overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
                    border: `2px solid ${i === activeImg ? GOLD : BD}`,
                  }}>
                    <img src={p} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: Info */}
          <div style={{ flex: 1, padding: isMobile ? '0' : 0 }}>
            {/* ── Dark Pricing Bar ── */}
            <div style={{
              background: 'linear-gradient(135deg, #1A2E28 0%, #1F3A32 100%)',
              padding: isMobile ? '16px 16px 14px' : '18px 20px 14px',
              borderRadius: isMobile ? 0 : '8px 8px 0 0',
              color: '#fff',
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,.6)' }}>{t('marketDetail.estimatedTotal')}</span>
                <span style={{ fontSize: 28, fontWeight: 700, fontFamily: 'var(--mono)', color: '#fff' }}>
                  ¥{priceCny.toLocaleString()}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, flexWrap: 'wrap', marginTop: 2 }}>
                {hasExhPrice && (
                  <span style={{
                    padding: '2px 6px', borderRadius: 3,
                    background: '#E74C3C', color: '#fff', fontSize: 10, fontWeight: 600,
                  }}>{t('marketDetail.exhibitionPrice')}</span>
                )}
                {hasExhPrice && (
                  <span style={{ color: 'rgba(255,255,255,.45)', textDecoration: 'line-through', fontSize: 11 }}>
                    ¥{originalPrice.toLocaleString()}
                  </span>
                )}
                <span style={{ color: '#6ECB8A', fontWeight: 600 }}>{discount}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, marginTop: 4 }}>
                <span style={{ color: 'rgba(255,255,255,.45)' }}>{t('marketDetail.marketPrice')} ¥{marketPrice.toLocaleString()}</span>
                {item.buyer_count > 0 && (
                  <span style={{ color: 'rgba(255,255,255,.45)' }}>
                    {t('marketDetail.buyerCount', { count: item.buyer_count })}
                  </span>
                )}
              </div>
              {grade && (
                <div style={{
                  position: 'absolute', ...(isMobile ? {} : {}),
                }}>
                </div>
              )}
            </div>

            {/* Grade badge - right side */}
            <div style={{
              background: '#fff', border: isMobile ? 'none' : `1px solid ${BD}`, borderTop: 'none',
              padding: isMobile ? '12px 16px' : '12px 20px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {grade && <Tag color={grade.color} style={{ borderRadius: 4, fontSize: 12, margin: 0 }}>{grade.short}</Tag>}
                {item.category && <Tag style={{ borderRadius: 4, margin: 0 }}>{item.category}</Tag>}
                {isOffer && <Tag color="green" style={{ borderRadius: 4, margin: 0 }}>{t('market.negotiable')}</Tag>}
              </div>
              {item.buyer_count > 0 && (
                <span style={{ fontSize: 12, color: MUTED }}>{t('marketDetail.buyerCount', { count: item.buyer_count })}</span>
              )}
            </div>

            {/* Product title & description */}
            <div style={{
              background: '#fff', border: isMobile ? 'none' : `1px solid ${BD}`, borderTop: 'none',
              padding: isMobile ? '0 16px 16px' : '0 20px 20px',
            }}>
              <h1 style={{
                fontSize: isMobile ? 18 : 22, fontWeight: 700, color: INK,
                fontFamily: 'var(--serif)', margin: '0 0 8px', lineHeight: 1.4,
              }}>
                {item.item_name}
              </h1>
              {item.item_description && (
                <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6, marginBottom: 8 }}>
                  {grade && <span>{grade.short}</span>}
                  {item.item_description && <span> {item.item_description.slice(0, 60)}</span>}
                </div>
              )}

              {/* Platform Guarantee Badge */}
              {item.platform_guaranteed ? (
                <div style={{
                  marginTop: 12, padding: '10px 12px', borderRadius: 6,
                  background: 'linear-gradient(135deg, #E8F8F5, #D5F5E3)',
                  border: `1px solid ${TEAL}30`, fontSize: 12,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 14 }}>🛡</span>
                    <span style={{ color: TEAL, fontWeight: 700, fontSize: 13 }}>{t('marketDetail.platformGuarantee')}</span>
                    {item.warranty_days > 0 && (
                      <span style={{
                        padding: '1px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                        background: '#fff', color: TEAL, border: `1px solid ${TEAL}40`,
                      }}>{t('marketDetail.warrantyDays', { days: item.warranty_days })}</span>
                    )}
                  </div>
                  <div style={{ color: MUTED, fontSize: 11 }}>
                    {t('marketDetail.guaranteeDesc')}
                    {item.warranty_days > 0 && ` · ${t('marketDetail.afterSalesWarranty', { days: item.warranty_days })}`}
                  </div>
                </div>
              ) : (
                <div style={{
                  marginTop: 12, padding: '10px 12px', background: '#F9F8F5', borderRadius: 6,
                  fontSize: 12, color: MUTED,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ color: TEAL, fontWeight: 600 }}>{t('marketDetail.itemAuthenticated')}</span>
                    <span>{t('marketDetail.shipBefore18')}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                    <span style={{ color: TEAL }}>{t('marketDetail.authenticGuarantee')}</span>
                    <span style={{ color: '#ccc' }}>·</span>
                    <span style={{ color: TEAL }}>{t('marketDetail.sfExpress')}</span>
                    <span style={{ color: '#ccc' }}>·</span>
                    <span>{t('marketDetail.noReturnPolicy')}</span>
                  </div>
                </div>
              )}
            </div>

            {/* ── Seller Card ("Ta的主人") ── */}
            <div style={{
              background: '#fff', border: isMobile ? 'none' : `1px solid ${BD}`, borderTop: 'none',
              padding: isMobile ? '16px 16px' : '16px 20px',
              borderRadius: isMobile ? 0 : '0 0 8px 8px',
            }}>
              <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>{t('marketDetail.itsOwner')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%', background: '#F5EDD8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 600, color: GOLD,
                }}>
                  {(item.seller_name || '?').charAt(0)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: INK }}>
                      {item.seller_name ? item.seller_name.charAt(0) + '**' : t('marketDetail.anonymousSeller')}
                    </span>
                    {item.merchant_credit_tier && (
                      <span style={{
                        padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 500,
                        background: item.merchant_credit_tier === 'premium' ? '#FFF8E1' : item.merchant_credit_tier === 'excellent' ? '#E8F8F5' : '#F5F5F5',
                        color: item.merchant_credit_tier === 'premium' ? GOLD : item.merchant_credit_tier === 'excellent' ? TEAL : MUTED,
                        border: `1px solid ${item.merchant_credit_tier === 'premium' ? GOLD : item.merchant_credit_tier === 'excellent' ? TEAL : '#ccc'}40`,
                      }}>
                        {item.merchant_credit_tier === 'premium' ? t('marketDetail.premiumMerchant') : item.merchant_credit_tier === 'excellent' ? t('marketDetail.excellentMerchant') : t('marketDetail.certifiedMerchant')}
                        {item.merchant_credit_score > 0 && ` ${item.merchant_credit_score}${t('marketDetail.points')}`}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                    {item.seller_sold_count || 0} {t('marketDetail.itemsOnSale')}
                  </div>
                </div>
                <button onClick={() => message.info(t('marketDetail.csComingSoon'))} style={{
                  padding: '6px 14px', borderRadius: 4, border: `1px solid ${BD}`,
                  background: '#fff', color: MUTED, fontSize: 12, cursor: 'pointer',
                }}>{t('marketDetail.contactSeller')}</button>
              </div>
            </div>

            {/* Exhibition Info Card */}
            {item.exhibition_name && (
              <div style={{
                background: '#fff', border: isMobile ? 'none' : `1px solid ${BD}`, borderTop: 'none',
                padding: isMobile ? '12px 16px' : '12px 20px',
              }}>
                <button onClick={() => {
                  if (item.booth_row_id && item.exh_id) navigate(`/exhibitions/${item.exh_id}/booth/${item.booth_row_id}`);
                  else if (item.exh_id) navigate(`/exhibitions/${item.exh_id}`);
                }} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                  background: 'linear-gradient(135deg, #1A2E28, #2A4A3E)',
                  border: 'none', textAlign: 'left',
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 6, flexShrink: 0,
                    background: 'rgba(255,255,255,.1)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: 16,
                  }}>🏛</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
                      {item.exhibition_name}
                      {item.booth_no && <span style={{ fontWeight: 400, color: 'rgba(255,255,255,.6)' }}> · {t('marketDetail.booth')} {item.booth_no}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', marginTop: 2 }}>
                      {item.merchant_name && `${item.merchant_name} · `}
                      {item.exhibition_venue || ''}
                      {item.exhibition_status === 'live' ? ` · ${t('exhibition.ongoing')}` : item.exhibition_status === 'upcoming' ? ` · ${t('exhibition.upcoming')}` : ''}
                    </div>
                  </div>
                  <span style={{ color: 'rgba(255,255,255,.4)', fontSize: 14 }}>›</span>
                </button>
              </div>
            )}

            {/* Desktop Buy Buttons */}
            {!isMobile && (
              <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                {isOffer && (
                  <button onClick={() => {
                    if (!isLoggedIn) { navigate('/login'); return; }
                    setOfferModalOpen(true);
                  }} style={{
                    flex: 1, padding: '14px 0', borderRadius: 6, border: `1px solid ${GOLD}`,
                    background: 'transparent', color: GOLD, fontSize: 15, fontWeight: 600, cursor: 'pointer',
                  }}>{t('market.makeOffer')}</button>
                )}
                <button onClick={handleBuy} style={{
                  flex: isOffer ? 1 : '1 1 auto', padding: '14px 0', borderRadius: 6, border: 'none',
                  background: TEAL, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(26,107,84,.3)',
                }}>{t('market.buyNow')}</button>
              </div>
            )}
          </div>
        </div>

        {/* ── TAB NAVIGATION ── */}
        <div style={{
          display: 'flex', borderBottom: `1px solid ${BD}`, marginTop: 24,
          padding: isMobile ? '0 16px' : 0, background: '#fff', borderRadius: '8px 8px 0 0',
          borderLeft: `1px solid ${BD}`, borderRight: `1px solid ${BD}`, borderTop: `1px solid ${BD}`,
        }}>
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              flex: 1, padding: '14px 0', border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: 14, fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? TEAL : MUTED,
              borderBottom: activeTab === tab.key ? `2px solid ${TEAL}` : '2px solid transparent',
              transition: 'all .2s',
            }}>{tab.label}</button>
          ))}
        </div>

        {/* ── TAB CONTENT ── */}
        <div style={{
          background: '#fff', border: `1px solid ${BD}`, borderTop: 'none',
          borderRadius: '0 0 8px 8px', padding: isMobile ? 16 : 24, marginBottom: 24,
        }}>
          {/* Trust banner inside tabs */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
            background: '#F9F8F5', borderRadius: 6, marginBottom: 16, fontSize: 12, color: MUTED,
            flexWrap: 'wrap',
          }}>
            <span style={{ color: TEAL }}>{t('marketDetail.authenticGuarantee')}</span>
            <span style={{ color: '#ddd' }}>·</span>
            <span style={{ color: TEAL }}>{t('marketDetail.sfExpress')}</span>
            <span style={{ color: '#ddd' }}>·</span>
            <span>{t('marketDetail.noReturnPolicy')}</span>
          </div>

          {/* 商品详情 tab */}
          {activeTab === 'detail' && (
            <div>
              {/* Product info table */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 600, fontSize: 15, color: INK, marginBottom: 12 }}>{t('marketDetail.productInfo')}</div>
                {[
                  [t('marketDetail.labelBrand'), item.brand || '—'],
                  [t('marketDetail.labelCategory'), item.category || '—'],
                  [t('marketDetail.labelGrade'), item.item_grade ? `${item.item_grade} ${t('market.gradeUnit')}` : '—'],
                  [t('marketDetail.labelPricingMode'), { fixed: t('market.fixedPrice'), offer: t('market.negotiable'), auction: t('market.auctionMode') }[item.pricing_mode] || '—'],
                  [t('marketDetail.labelSource'), item.exhibition_name ? `${t('marketDetail.sourceExhibition')}: ${item.exhibition_name}` : item.source_type === 'order' ? t('marketDetail.sourceAuction') : t('marketDetail.sourceConsignment')],
                  [t('marketDetail.labelStorage'), item.storage_type === 'platform' ? t('marketDetail.storagePlatform') : t('marketDetail.storageSeller')],
                  [t('marketDetail.labelItemNo'), item.item_no],
                ].map(([k, v]) => (
                  <div key={k} style={{
                    display: 'flex', justifyContent: 'space-between', padding: '9px 0',
                    borderBottom: `1px solid ${BD}40`, fontSize: 13,
                  }}>
                    <span style={{ color: MUTED }}>{k}</span>
                    <span style={{ color: INK, fontFamily: k === t('marketDetail.labelItemNo') ? 'var(--mono)' : 'inherit' }}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Description */}
              {item.item_description && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15, color: INK, marginBottom: 8 }}>{t('marketDetail.productDescription')}</div>
                  <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{item.item_description}</div>
                </div>
              )}

              {/* Condition breakdown card */}
              {grade && (
                <div style={{
                  background: '#F9F8F5', borderRadius: 8, padding: 16, marginTop: 20,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: INK }}>{t('marketDetail.conditionDetails')}</span>
                    <Tag color={grade.color} style={{ margin: 0, borderRadius: 4 }}>{grade.label}</Tag>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0' }}>
                    {CONDITION_AREAS.map((ca, i) => (
                      <div key={ca.key} style={{
                        display: 'flex', justifyContent: 'space-between', padding: '8px 10px',
                        background: i % 2 === 0 ? '#fff' : '#FAFAF7',
                        borderBottom: `1px solid ${BD}20`,
                      }}>
                        <span style={{ fontSize: 12, color: MUTED }}>{ca.area}</span>
                        <span style={{ fontSize: 12, fontWeight: 500, color: INK }}>{condition[ca.key]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Trust badges */}
              <div style={{
                display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                gap: 10, marginTop: 24, padding: '16px 0', borderTop: `1px solid ${BD}40`,
              }}>
                {(item.platform_guaranteed ? [
                  { icon: '🛡', title: t('marketDetail.badgePlatformGuarantee'), desc: t('marketDetail.badgeAuthFirst') },
                  { icon: '🔍', title: t('marketDetail.badgeProfessionalInspection'), desc: t('marketDetail.badgeExpertTeam') },
                  { icon: '📦', title: t('marketDetail.badgeSfDelivery'), desc: t('marketDetail.badgeInsuredShipping') },
                  { icon: '↩️', title: t('marketDetail.badgeWarrantyDays', { days: item.warranty_days || 7 }), desc: t('marketDetail.badgeReturnIfMismatch') },
                ] : [
                  { icon: '🔍', title: t('marketDetail.badgeProfessionalInspection'), desc: t('marketDetail.badgeExpertTeam') },
                  { icon: '🛡', title: t('marketDetail.authenticGuarantee'), desc: t('marketDetail.badgeFakeCompensation') },
                  { icon: '📦', title: t('marketDetail.badgeSfDelivery'), desc: t('marketDetail.badgeInsuredShipping') },
                  { icon: '↩️', title: t('marketDetail.badge7DayReturn'), desc: t('marketDetail.badgeReturnIfMismatch') },
                ]).map(b => (
                  <div key={b.title} style={{ textAlign: 'center', padding: '10px 0' }}>
                    <div style={{ fontSize: 22, marginBottom: 4 }}>{b.icon}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: INK }}>{b.title}</div>
                    <div style={{ fontSize: 10, color: MUTED }}>{b.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 鉴别报告 tab */}
          {activeTab === 'appraisal' && (
            <div>
              {/* Seller card inside appraisal */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: 14,
                background: '#FAFAF7', borderRadius: 8, marginBottom: 16,
              }}>
                <div style={{ fontSize: 12, color: MUTED }}>{t('marketDetail.itsOwner')}</div>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', background: '#F5EDD8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 600, color: GOLD,
                }}>
                  {(item.seller_name || '?').charAt(0)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: INK }}>
                      {item.seller_name ? item.seller_name.charAt(0) + '**' : t('marketDetail.anonymousSeller')}
                    </span>
                    <span style={{
                      padding: '1px 6px', borderRadius: 3, fontSize: 10,
                      background: '#FFF8E1', color: GOLD, fontWeight: 500,
                    }}>{t('marketDetail.premiumSeller')}</span>
                  </div>
                  <div style={{ fontSize: 11, color: MUTED }}>{item.seller_sold_count || 0} {t('marketDetail.itemsOnSale')}</div>
                </div>
              </div>

              {/* Appraisal report card */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px',
                background: 'linear-gradient(135deg, #1A2E28 0%, #1F3A32 100%)',
                borderRadius: 10, marginBottom: 20, color: '#fff',
              }}>
                <div style={{
                  width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
                  border: '2px solid rgba(255,255,255,.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 8, fontWeight: 700, lineHeight: 1.2, textAlign: 'center',
                  background: 'rgba(255,255,255,.1)', color: '#A8D8B9',
                }}>CERTIFIED<br/>AUTHENTIC</div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>{t('marketDetail.officialAppraisalReport')}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)' }}>
                    {t('marketDetail.reportIssuedBy')}
                  </div>
                </div>
              </div>

              {/* Grade + condition */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 600, fontSize: 15, color: INK, marginBottom: 6 }}>
                  {grade ? grade.short : item.item_grade || '—'}
                </div>
                <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
                  {grade ? grade.desc : ''}
                  {item.item_description && `. ${item.item_description.slice(0, 100)}`}
                </div>
              </div>

              {/* Detail inspection */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: TEAL, marginBottom: 10 }}>{t('marketDetail.detailInspection')}</div>
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6,
                }}>
                  {photos.slice(0, 6).map((p, i) => (
                    <div key={i} style={{
                      paddingTop: '100%', position: 'relative', borderRadius: 6, overflow: 'hidden',
                      background: '#F5EDD8',
                    }}>
                      <img src={p} alt="" style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover',
                      }} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Appraisal details */}
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: INK, marginBottom: 10 }}>{t('marketDetail.inspectionRecord')}</div>
                {[
                  [t('marketDetail.inspResult'), t('marketDetail.authentic')],
                  [t('marketDetail.inspGrade'), grade ? grade.label : '—'],
                  [t('marketDetail.inspMethod'), t('marketDetail.inspMethodValue')],
                  [t('marketDetail.inspAppraiser'), t('marketDetail.inspAppraiserValue')],
                  [t('marketDetail.inspDate'), new Date().toISOString().slice(0, 10)],
                ].map(([k, v]) => (
                  <div key={k} style={{
                    display: 'flex', justifyContent: 'space-between', padding: '8px 0',
                    borderBottom: `1px solid ${BD}40`, fontSize: 13,
                  }}>
                    <span style={{ color: MUTED }}>{k}</span>
                    <span style={{ color: k === t('marketDetail.inspResult') ? TEAL : INK, fontWeight: k === t('marketDetail.inspResult') ? 600 : 400 }}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Inspection points */}
              <div style={{ marginTop: 20 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: INK, marginBottom: 10 }}>{t('marketDetail.inspectionPoints')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                  {[
                    { point: t('marketDetail.pointSerial'), status: t('marketDetail.pointPass'), desc: t('marketDetail.pointSerialDesc') },
                    { point: t('marketDetail.pointMaterial'), status: t('marketDetail.pointPass'), desc: t('marketDetail.pointMaterialDesc') },
                    { point: t('marketDetail.pointCraftsmanship'), status: t('marketDetail.pointPass'), desc: t('marketDetail.pointCraftsmanshipDesc') },
                    { point: t('marketDetail.pointHardware'), status: t('marketDetail.pointPass'), desc: t('marketDetail.pointHardwareDesc') },
                  ].map(p => (
                    <div key={p.point} style={{
                      padding: 12, background: '#F9F8F5', borderRadius: 6,
                      border: `1px solid ${BD}40`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: INK }}>{p.point}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: TEAL }}>{p.status}</span>
                      </div>
                      <div style={{ fontSize: 11, color: MUTED }}>{p.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 规格参数 tab */}
          {activeTab === 'specs' && (
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, color: INK, marginBottom: 14 }}>{t('marketDetail.basicSpecs')}</div>
              {[
                ['品牌 Brand', item.brand || '—'],
                ['品类 Category', item.category || '—'],
                ['品相 Condition', grade ? grade.label : (item.item_grade || '—')],
                ['商品编号 Item No.', item.item_no],
              ].map(([k, v]) => (
                <div key={k} style={{
                  display: 'flex', padding: '10px 0', borderBottom: `1px solid ${BD}40`,
                }}>
                  <span style={{ width: isMobile ? 120 : 180, flexShrink: 0, fontSize: 13, color: MUTED }}>{k}</span>
                  <span style={{ fontSize: 13, color: INK, fontWeight: 500 }}>{v}</span>
                </div>
              ))}

              {item.category === '腕表' && (
                <>
                  <div style={{ fontWeight: 600, fontSize: 15, color: INK, marginBottom: 14, marginTop: 24 }}>{t('marketDetail.watchSpecs')}</div>
                  {[
                    ['表径 Case Size', '—'],
                    ['表壳材质 Case Material', '—'],
                    ['机芯类型 Movement', '—'],
                    ['防水深度 Water Resistance', '—'],
                    ['表带材质 Strap', '—'],
                  ].map(([k, v]) => (
                    <div key={k} style={{
                      display: 'flex', padding: '10px 0', borderBottom: `1px solid ${BD}40`,
                    }}>
                      <span style={{ width: isMobile ? 120 : 180, flexShrink: 0, fontSize: 13, color: MUTED }}>{k}</span>
                      <span style={{ fontSize: 13, color: INK }}>{v}</span>
                    </div>
                  ))}
                </>
              )}
              {item.category === '箱包' && (
                <>
                  <div style={{ fontWeight: 600, fontSize: 15, color: INK, marginBottom: 14, marginTop: 24 }}>{t('marketDetail.bagSpecs')}</div>
                  {[
                    ['尺寸 Size', '—'],
                    ['材质 Material', '—'],
                    ['颜色 Color', '—'],
                    ['五金 Hardware', '—'],
                  ].map(([k, v]) => (
                    <div key={k} style={{
                      display: 'flex', padding: '10px 0', borderBottom: `1px solid ${BD}40`,
                    }}>
                      <span style={{ width: isMobile ? 120 : 180, flexShrink: 0, fontSize: 13, color: MUTED }}>{k}</span>
                      <span style={{ fontSize: 13, color: INK }}>{v}</span>
                    </div>
                  ))}
                </>
              )}

              <div style={{
                marginTop: 20, padding: 12, background: '#F9F8F5', borderRadius: 6,
                fontSize: 12, color: MUTED, lineHeight: 1.7,
              }}>
                {t('marketDetail.specsDisclaimer')}
              </div>
            </div>
          )}

          {/* 相似推荐 tab */}
          {activeTab === 'similar' && (
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, color: INK, marginBottom: 14 }}>
                {item.category || t('marketDetail.sameCategory')} {t('marketDetail.similarProducts')}
              </div>
              {similar.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                  gap: 12,
                }}>
                  {similar.map(s => {
                    const sPhoto = s.photos?.[0] || `https://placehold.co/300x300/F5EDD8/B8892A?text=${encodeURIComponent(s.brand || '?')}`;
                    return (
                      <div key={s.id} onClick={() => { navigate(`/market/${s.id}`); window.scrollTo(0, 0); }} style={{
                        cursor: 'pointer', background: '#FAFAF7', borderRadius: 8,
                        border: `1px solid ${BD}40`, overflow: 'hidden',
                        transition: 'box-shadow .2s',
                      }}>
                        <div style={{ paddingTop: '100%', position: 'relative', background: '#F5EDD8' }}>
                          <img src={sPhoto} alt="" style={{
                            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover',
                          }} />
                          {s.item_grade && (
                            <span style={{
                              position: 'absolute', top: 6, left: 6, padding: '2px 6px', borderRadius: 3,
                              background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 10,
                            }}>{s.item_grade}级</span>
                          )}
                        </div>
                        <div style={{ padding: '10px 10px 12px' }}>
                          <div style={{ fontSize: 11, color: MUTED, fontFamily: "'Georgia', serif" }}>{s.brand}</div>
                          <div style={{
                            fontSize: 12, fontWeight: 500, color: INK, marginTop: 2,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>{s.item_name}</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: GOLD, fontFamily: 'var(--mono)', marginTop: 6 }}>
                            ¥{Number(s.price_cny).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 0', color: MUTED, fontSize: 13 }}>
                  {t('marketDetail.noSimilarProducts')}
                </div>
              )}
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <button onClick={() => navigate('/market')} style={{
                  padding: '10px 32px', borderRadius: 6, border: `1px solid ${TEAL}`,
                  background: 'transparent', color: TEAL, fontSize: 13, cursor: 'pointer',
                }}>{t('marketDetail.browseMore')}</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MOBILE BOTTOM BAR */}
      {isMobile && (
        <div style={{
          position: 'fixed', bottom: 56, left: 0, right: 0, background: '#fff',
          borderTop: `1px solid ${BD}`, padding: '0', display: 'flex', alignItems: 'stretch', zIndex: 101,
          boxShadow: '0 -2px 8px rgba(0,0,0,.06)',
        }}>
          {/* Left icon buttons */}
          <div style={{ display: 'flex', borderRight: `1px solid ${BD}` }}>
            <button onClick={() => navigate(`/consignment/submit?category=${item.category || ''}&brand=${item.brand || ''}`)} style={{
              width: 52, border: 'none', background: 'transparent', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              padding: '10px 0', gap: 2,
            }}>
              <span style={{ fontSize: 18 }}>💰</span>
              <span style={{ fontSize: 9, color: MUTED }}>{t('marketDetail.sellSame')}</span>
            </button>
            <button onClick={() => message.info(t('marketDetail.csComingSoon'))} style={{
              width: 52, border: 'none', background: 'transparent', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              padding: '10px 0', gap: 2,
            }}>
              <span style={{ fontSize: 18 }}>💬</span>
              <span style={{ fontSize: 9, color: MUTED }}>{t('marketDetail.consult')}</span>
            </button>
          </div>
          {/* Right action buttons */}
          <div style={{ flex: 1, display: 'flex' }}>
            {isOffer && (
              <button onClick={() => {
                if (!isLoggedIn) { navigate('/login'); return; }
                setOfferModalOpen(true);
              }} style={{
                flex: 1, border: 'none', background: '#2A2A2A', color: '#fff',
                fontSize: 15, fontWeight: 600, cursor: 'pointer', padding: '14px 0',
              }}>{t('market.makeOffer')}</button>
            )}
            <button onClick={handleBuy} style={{
              flex: 1, border: 'none',
              background: TEAL, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
              padding: '14px 0',
            }}>{t('market.buyNow')}</button>
          </div>
        </div>
      )}

      {/* OFFER MODAL */}
      <Modal open={offerModalOpen} onCancel={() => setOfferModalOpen(false)} footer={null} title={null} width={400} centered>
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 18, fontWeight: 700, color: INK, marginBottom: 16 }}>{t('market.makeOffer')}</div>
          <div style={{ background: '#F9F6F0', borderRadius: 8, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: MUTED }}>{t('marketDetail.listPrice')} ¥{priceCny.toLocaleString()}</div>
            {item.min_price_cny && <div style={{ fontSize: 12, color: RED }}>{t('marketDetail.sellerMinAccept')} ¥{Number(item.min_price_cny).toLocaleString()}</div>}
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: MUTED, marginBottom: 6 }}>{t('marketDetail.yourOffer')} (CNY)</div>
            <input
              type="number" value={offerPrice}
              onChange={e => setOfferPrice(e.target.value)}
              placeholder={t('marketDetail.offerPlaceholder')}
              style={{
                width: '100%', padding: '10px 12px', border: `1px solid ${BD}`, borderRadius: 6,
                fontSize: 16, fontFamily: 'var(--mono)', outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
          <button onClick={handleOffer} disabled={submitting} style={{
            width: '100%', padding: '14px 0', borderRadius: 6, border: 'none',
            background: TEAL, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
            opacity: submitting ? 0.6 : 1,
          }}>{submitting ? '提交中...' : '提交出价'}</button>
        </div>
      </Modal>
    </div>
  );
}
